/**
 * Vuex ストアの状態をストレージに保存・復元するプラグイン
 *
 * vuex-persistedstate（依存の deepmerge にプロトタイプ汚染の脆弱性があり修正版が無い）の置き換え。
 * 本プロジェクトで使用していた範囲（key / storage の指定のみ）について、vuex-persistedstate 4.1.0 と同じ動作をする。
 * - 起動時: ストレージが使用可能か確認し、保存済みの状態があればストアの初期状態にマージして復元する
 *   （オブジェクトは再帰的にマージ、配列は保存済みの値で置き換え）
 * - mutation 実行のたび: ストアの状態全体を JSON にしてストレージに保存する
 * deepmerge と異なり、"__proto__" / "constructor" / "prototype" のキーはマージしない。
 */

// プロトタイプ汚染につながるキー
const UNSAFE_KEYS = ['__proto__', 'constructor', 'prototype']

// マージ対象のオブジェクトか（deepmerge の既定の判定と同じ）
function isMergeableObject(value) {
  if (!value || typeof value !== 'object') return false
  const tag = Object.prototype.toString.call(value)
  return tag !== '[object RegExp]' && tag !== '[object Date]'
}

// 列挙可能な自身のキー（シンボルを含む）
function getKeys(target) {
  const symbols = Object.getOwnPropertySymbols(target).filter((symbol) =>
    Object.prototype.propertyIsEnumerable.call(target, symbol)
  )
  return Object.keys(target).concat(symbols)
}

function propertyIsOnObject(object, property) {
  try {
    return property in object
  } catch {
    return false
  }
}

// プロトタイプ上にしか無いプロパティ、または列挙できない自身のプロパティへの上書きか
function propertyIsUnsafe(target, key) {
  return (
    propertyIsOnObject(target, key) &&
    !(
      Object.prototype.hasOwnProperty.call(target, key) &&
      Object.prototype.propertyIsEnumerable.call(target, key)
    )
  )
}

function mergeObject(target, source) {
  const destination = {}
  if (isMergeableObject(target)) {
    getKeys(target).forEach((key) => {
      destination[key] = target[key]
    })
  }
  getKeys(source).forEach((key) => {
    if (UNSAFE_KEYS.includes(key) || propertyIsUnsafe(target, key)) {
      return
    }
    destination[key] =
      propertyIsOnObject(target, key) && isMergeableObject(source[key])
        ? merge(target[key], source[key])
        : source[key]
  })
  return destination
}

// deepmerge(target, source, { arrayMerge: (target, source) => source, clone: false }) と同じマージ
function merge(target, source) {
  const sourceIsArray = Array.isArray(source)
  if (sourceIsArray !== Array.isArray(target) || sourceIsArray) {
    return source
  }
  return mergeObject(target, source)
}

function getSavedState(key, storage) {
  const value = storage.getItem(key)
  try {
    if (typeof value === 'string') return JSON.parse(value)
    if (typeof value === 'object') return value
    return undefined
  } catch {
    return undefined
  }
}

/**
 * プラグインを生成する
 * @param {Object} options key: 保存キー、storage: 保存先（既定は window.localStorage）
 * @returns Vuex プラグイン
 */
export default function createPersistedState(options = {}) {
  const storage = options.storage || window.localStorage
  const key = options.key || 'vuex'

  // ストレージが使用可能か確認する（使用できない場合は例外）
  storage.setItem('@@', 1)
  storage.removeItem('@@')

  return (store) => {
    const savedState = getSavedState(key, storage)
    if (typeof savedState === 'object' && savedState !== null) {
      store.replaceState(merge(store.state, savedState))
    }

    store.subscribe((mutation, state) => {
      storage.setItem(key, JSON.stringify(state))
    })
  }
}
